<template>
  <div
    v-if="hasRoles('R&D', 'HRL')"
    class="q-pa-lg flex flex-center"
  >
    <q-form
      ref="validateForm"
      @submit="onSubmit"
      greedy
    >
        <div class="text-h6" align="center">
           Preview and Send Emails
        </div>
      <div class="row">
        <q-select
          label="Email Template *"
          class="col-12 q-pa-xs"
          v-model="manualEmail"
          use-input
          input-debounce="0"
          @filter="manualEmailFilter"
          :options="manualEmailListOptions"
          emit-value
          @update:model-value="(val) =>
          onEmailTemplateUpdate(val, userEmail, sessionId)"
          :rules="[val => !!val || 'Email template is required.']"
        />
        <q-select
          label="Session Name *"
          class="col-12 q-pa-xs"
          v-model="sessionId"
          :options="sessionOptions"
          emit-value
          map-options
          @update:model-value="(val) => onEmailTemplateUpdate(manualEmail, userEmail, val)"
          :rules="[val => !!val || 'Session is required.']"
        />
        <q-select
          label="Preview Email *"
          class="col-12 q-pa-xs"
          v-model="userEmail"
          use-input
          input-debounce="0"
          @filter="applicantEmailFilter"
          :options="emailSelectedForSend"
          emit-value
          @update:model-value="(val) =>
          onEmailTemplateUpdate(manualEmail, val, sessionId)"
          :rules="[val => !!val || 'Preview email is required.']"
        />
        <q-select
          label="Email(s) Selected for Send *"
          class="col-12 q-pa-xs"
          v-model="emailSelectedForSend"
          multiple
          use-input
          use-chips
          input-debounce="0"
          @filter="applicantEmailFilter"
          :options="applicantEmailListOptions"
          emit-value
          :rules="[val => val?.length !== 0 || 'Email(s) required.']"
        />
        <q-input
          v-model.trim="emailTemplate.from"
          class="col-12 q-pa-xs"
          label="From"
          readonly
        />
        <q-input
          v-model.trim="emailTemplate.cc"
          class="col-12 q-pa-xs"
          label="Cc"
          readonly
        />
        <q-input
          v-model.trim="emailTemplate.bcc"
          class="col-12 q-pa-xs"
          label="Bcc"
          readonly
        />
        <q-input
          v-model.trim="emailTemplate.attachmentName"
          class="col-12 q-pa-xs"
          label="Attachment"
          readonly
        />
        <q-input
          v-model="emailTemplate.subject"
          class="col-12 q-pa-xs"
          label="Subject"
          readonly
        />
        <div class="text-subtitle2 q-ma-sm">
          Email Body:
        </div>
        <q-editor
          :toolbar="[]"
          v-model="emailTemplate.body"
          class="col-12 q-pa-xs"
          label="Body"
          readonly
        />
      </div>
      <div class="q-gutter-sm flex flex-center q-pa-md">
         <q-btn
            flat
            label="Cancel"
            v-close-popup
            color="primary"
          />
          <q-btn
            flat
            label="Send"
            color ="positive"
            @click="onSubmit"
          />
      </div>
    </q-form>
  </div>
</template>

<script setup>
import {
  defineProps,
  defineEmits,
  ref,
  onMounted,
  onUnmounted,
} from 'vue';
import { useQuasar } from 'quasar';
import useAuth from '@/hooks/auth';
import useEmailTemplate from '@/hooks/email';
import useSession from '@/hooks/session';
import useUiUtilities from '@/utilities/uiUtilities';

const props = defineProps({
  applicantEmailList: Array,
  sessionsDropDown: Array,
});

const emit = defineEmits(['onSuccess']);

const $q = useQuasar();
const { hasRoles } = useAuth();
const {
  emailTemplate,
  getExampleEmail,
  getManualEmailList,
  resetCurrentEmailTemplate,
  sendManualEmails,
} = useEmailTemplate();
const { fetchSessionNameIds } = useSession();
const { notifyResult } = useUiUtilities();

const isAdd = ref(true);
const validateForm = ref(false);
const manualEmail = ref(null);
const manualEmailList = ref(null);
const userEmail = ref();
const sessionId = ref('');
const sessionOptions = ref([]);
const emailSelectedForSend = ref([]);

onMounted(async () => {
  isAdd.value = !emailTemplate.value.emailTemplateId || emailTemplate.value.emailTemplateId === 0;
  if (props.applicantEmailList && props.applicantEmailList.length > 0) {
    [userEmail.value] = props.applicantEmailList;
  }
  const sessionNameIds = await fetchSessionNameIds();
  const filteredSessionNames = ref([]);
  if (props.sessionsDropDown[0] === 0) {
    filteredSessionNames.value = sessionNameIds;
  } else {
    filteredSessionNames.value = sessionNameIds.filter(
      (session) => props.sessionsDropDown.includes(session.sessionId),
    );
  }
  sessionOptions.value = filteredSessionNames.value.map((session) => ({
    label: session.sessionName,
    value: session.sessionId,
  }));

  if (sessionOptions.value.length === 1) {
    [sessionId.value] = sessionOptions.value.value;
  }
});

onUnmounted(() => {
  resetCurrentEmailTemplate();
});

const onSubmit = async () => {
  const isValidated = await validateForm.value.validate();
  if (isValidated) {
    let response = '';
    $q.loading.show();
    response = await sendManualEmails(
      emailSelectedForSend.value, emailTemplate.value.emailTemplateName, sessionId.value,
    );
    notifyResult(response.status, response, true, 'Email(s) sent successfully');
    $q.loading.hide();
    if (response.status === 200 || response.status === 201) {
      emit('onSuccess');
    }
    return response;
  }

  return false;
};

const onEmailTemplateUpdate = async (emailName, email, curSessionId) => {
  if (!email || !emailName || !curSessionId) {
    return;
  }
  $q.loading.show();
  const response = await getExampleEmail(emailName, email, curSessionId);
  if (response.status === 200) {
    emailTemplate.value = response.data.data;
  } else {
    notifyResult(response.status, response);
    resetCurrentEmailTemplate();
  }
  $q.loading.hide();
};

manualEmailList.value = await getManualEmailList();

const applicantEmailListOptions = ref(props.applicantEmailList);
emailSelectedForSend.value = applicantEmailListOptions.value;
const applicantEmailFilter = (val, update) => {
  update(() => {
    const needle = val.toLowerCase();
    applicantEmailListOptions.value = props.applicantEmailList.filter(
      (v) => v.toLowerCase().indexOf(needle) > -1,
    );
  });
};

const manualEmailListOptions = ref(manualEmailList.value);
const manualEmailFilter = (val, update) => {
  update(() => {
    const needle = val.toLowerCase();
    manualEmailListOptions.value = manualEmailList.value.filter(
      (v) => v.toLowerCase().indexOf(needle) > -1,
    );
  });
};
</script>

<!-- .variable class should not be updated.
  If updating, server regex must match the same format for email template variables to function. -->
<style lang="css">
.variable {
  background: #11BBA4;
  color: white;
  padding: 3px;
  border-radius: 5px;
}
</style>
